import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Calendar, Building } from "lucide-react";
import SeoCopyBlock from "@/components/SeoCopyBlock";
import { SEO_COPY } from "@/data/seoCopy";
import useSEO from "@/hooks/useSEO";
import QuickQuote from "@/components/QuickQuote";
import { getExpoBySlug, getCountryBySlug, expos as allExpos } from "@/data/clientsData";
import { thumb } from "@/lib/img";
import { breadcrumbSchema, eventServiceSchema, organizationSchema, videoObjectSchema } from "@/lib/seo-schemas";

const ExpoPage = () => {
  const { slug } = useParams();
  const expo = getExpoBySlug(slug);
  const country = expo ? getCountryBySlug(expo.country) : null;

  useSEO({
    title: expo
      ? SEO_COPY[`expo-${expo.slug}`]?.metaTitle || `${expo.name.split(" — ")[0]} Stand Builder, ${expo.city} | Stagehook Global`
      : "Expo not found",
    description: expo
      ? SEO_COPY[`expo-${expo.slug}`]?.metaDescription || `Stagehook Global is an experienced ${expo.name} booth builder & exhibition stall designer in ${expo.city}, ${expo.countryName}. ${expo.description}`
      : "",
    keywords: expo
      ? `${expo.name} stand builder, ${expo.name} booth design, exhibition stall ${expo.city}, expo booth builder ${expo.countryName}, ${expo.name} contractor, ${expo.name} stall fabricator`
      : "",
    image: expo?.heroImage,
    path: expo ? `/expo/${expo.slug}` : undefined,
    jsonLd: expo
      ? [
          organizationSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: expo.countryName, path: `/country/${expo.country}` },
            { name: expo.name, path: `/expo/${expo.slug}` },
          ]),
          eventServiceSchema(expo),
          ...(expo.video ? [videoObjectSchema(expo)] : []),
        ]
      : null,
  });

  if (slug === "ise-amsterdam") return <Navigate to="/expo/ise-barcelona" replace />;
  if (!expo) return <Navigate to="/404" replace />;

  // Related expos: same country, exclude current
  const related = allExpos
    .filter((e) => e.country === expo.country && e.slug !== expo.slug)
    .slice(0, 3);

  return (
    <main data-testid="expo-page" className="pt-[72px]">
      {/* HERO */}
      <section className="relative min-h-[68vh] flex items-end overflow-hidden grain">
        <img
          src={expo.heroImage}
          alt={`${expo.name} exhibition stand built by Stagehook Global in ${expo.city}`}
          fetchPriority="high"
          decoding="async"
          data-testid="expo-hero-img"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,7,10,0.55) 0%, rgba(7,7,10,0.75) 55%, rgba(7,7,10,0.98) 100%)",
          }}
        />

        <div className="shg-container w-full relative z-10 py-20">
          <div className="flex items-center gap-3 text-sm mb-8">
            <Link
              to="/"
              data-testid="breadcrumb-home"
              className="text-[var(--text-muted)] hover:text-[var(--red)]"
            >
              Home
            </Link>
            <span className="text-[var(--text-muted)]">/</span>
            <Link
              to={`/country/${expo.country}`}
              data-testid="breadcrumb-country"
              className="text-[var(--text-muted)] hover:text-[var(--red)]"
            >
              {expo.countryName}
            </Link>
            <span className="text-[var(--text-muted)]">/</span>
            <span className="text-white">{expo.name}</span>
          </div>

          <div className="flex items-center gap-3 mb-5">
            <span className="text-2xl">{country?.flag}</span>
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--red)]">
              EXPO BOOTH BUILDER · {expo.city.toUpperCase()}
            </div>
            {expo.latest && (
              <span
                data-testid="latest-handover-badge"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] px-2.5 py-1 rounded-full text-white"
                style={{ background: "var(--red)" }}
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                </span>
                LATEST HANDOVER
              </span>
            )}
          </div>

          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] mb-6 max-w-5xl">
            {expo.name}<br />
            <span style={{ color: "var(--red)" }}>stand builders.</span>
          </h1>
          <p className="text-lg text-[var(--text-soft)] max-w-2xl">
            {expo.description}
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <span className="flag-chip"><Calendar size={12} /> {expo.month}</span>
            <span className="flag-chip"><MapPin size={12} /> {expo.city}, {expo.countryName}</span>
            <span className="flag-chip"><Building size={12} /> {expo.venue}</span>
            <span className="flag-chip">{expo.clients.length} {expo.clients.length === 1 ? "stand" : "stands"} built</span>
          </div>
        </div>
      </section>

      {/* HANDOVER VIDEO */}
      {expo.video && (
        <section
          data-testid="handover-video-section"
          className="py-24"
          style={{ background: "var(--bg-soft)" }}
        >
          <div className="shg-container">
            <div className="mb-10 max-w-3xl">
              <div className="section-tag mb-4">
                <span className="dot" />Latest stall handover
                {expo.handoverDate ? ` · ${expo.handoverDate}` : ""}
              </div>
              <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">
                Fresh off the floor<br />
                <span style={{ color: "var(--red)" }}>at {expo.name}</span>.
              </h2>
            </div>

            <div
              className="rounded-2xl overflow-hidden"
              style={{ border: "1px solid var(--line-strong)" }}
            >
              <video
                data-testid="handover-video-player"
                src={expo.video}
                controls
                playsInline
                preload="metadata"
                poster={expo.heroImage}
                className="w-full h-auto bg-black block"
              />
            </div>
          </div>
        </section>
      )}

      <SeoCopyBlock id={`expo-${expo.slug}`} />

      <section data-testid="expo-quote-section" className="py-16">
        <div className="shg-container max-w-3xl"><QuickQuote expoName={expo.name} source="expo" /></div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section data-testid="related-expos" className="py-20" style={{ background: "var(--bg-soft)" }}>
          <div className="shg-container">
            <div className="flex items-end justify-between mb-10">
              <h3 className="font-display text-3xl md:text-4xl">
                More expos in <span style={{ color: "var(--red)" }}>{expo.countryName}</span>
              </h3>
              <Link to={`/country/${expo.country}`} className="shg-link text-sm">
                View all →
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/expo/${r.slug}`}
                  data-testid={`related-expo-${r.slug}`}
                  className="shg-card group block"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={thumb(r.heroImage)}
                      alt={`${r.name} stand builder in ${r.city}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] mb-2">
                      {r.city.toUpperCase()} · {r.month.toUpperCase()}
                    </div>
                    <h4 className="font-display text-2xl group-hover:text-[var(--red)] transition-colors">
                      {r.name}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default ExpoPage;
